import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/** A toolbar button that opens a panel. Closes on Escape or a click outside. */
export function Menu({
  label,
  icon,
  count,
  align = 'left',
  active = false,
  children,
}: {
  label: string;
  icon?: ReactNode;
  count?: ReactNode;
  align?: 'left' | 'right';
  active?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="menu-wrap" ref={ref}>
      <button
        type="button"
        className={`btn${active ? ' active' : ''}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        {icon}
        {label}
        {count !== undefined && <span className="count">{count}</span>}
        <ChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <div className={`menu${align === 'right' ? ' right' : ''}`} id={id} role="menu">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
